import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Globe, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function PortalLanding() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY') {
      // Skip reCAPTCHA if not configured
      setRecaptchaReady(true);
      return;
    }

    // Check if already loaded
    if (window.grecaptcha) {
      setRecaptchaReady(true);
      return;
    }

    // Set a timeout to proceed even if reCAPTCHA fails to load (e.g., blocked in preview env)
    const timeout = setTimeout(() => {
      if (!recaptchaReady) {
        console.warn('reCAPTCHA failed to load in time, proceeding without it');
        setRecaptchaReady(true);
      }
    }, 5000);

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        clearTimeout(timeout);
        setRecaptchaReady(true);
      });
    };
    script.onerror = () => {
      console.warn('reCAPTCHA script failed to load');
      clearTimeout(timeout);
      setRecaptchaReady(true);
    };
    document.head.appendChild(script);

    return () => {
      clearTimeout(timeout);
    };
  }, [recaptchaReady]);

  const getRecaptchaToken = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY') {
      return null; // Skip if not configured
    }
    
    if (!window.grecaptcha) {
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'portal_magic_link' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      // Get reCAPTCHA token (if configured)
      const recaptchaToken = await getRecaptchaToken();
      
      await axios.post(`${API}/portal/auth/send-link`, { 
        email,
        recaptcha_token: recaptchaToken 
      });
      setSent(true);
      toast.success("Check your email for the magic link!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send link. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Check Your Email</h2>
            <p className="text-muted-foreground mb-6">
              We sent a magic link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              Click the link in your email to continue building your website. The link expires in 24 hours.
            </p>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => { setSent(false); setEmail(""); }}
            >
              Use a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Value Prop */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Build Your Professional Website in Minutes
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Get a beautiful, mobile-ready website for your local business. No coding required. No sales calls needed.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-green-600" />
                </div>
                <p className="text-slate-700">
                  <strong>Preview First</strong> — See your site before you pay
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-green-600" />
                </div>
                <p className="text-slate-700">
                  <strong>One-Time Payment</strong> — $600 for your complete site
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-green-600" />
                </div>
                <p className="text-slate-700">
                  <strong>Optional Hosting</strong> — $15/mo if you need us to host it
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-500">
              You own your website files. Host it yourself or let us handle it. Either way, you are in control.
            </p>
          </div>

          {/* Right: Email Form */}
          <Card className="shadow-xl">
            <CardContent className="pt-8 pb-8">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe size={24} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold">Get Started</h2>
                <p className="text-muted-foreground mt-1">
                  Enter your email to begin
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-lg"
                  disabled={loading || !recaptchaReady}
                  data-testid="portal-email-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-lg"
                  disabled={loading || !recaptchaReady}
                  data-testid="portal-start-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Sending Link...
                    </>
                  ) : !recaptchaReady ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Loading...
                    </>
                  ) : (
                    <>
                      Start Building
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-center text-muted-foreground mt-4">
                We will send you a secure link to continue. No password needed.
              </p>
              
              {/* reCAPTCHA notice - required by Google */}
              {RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY' && (
                <p className="text-[10px] text-center text-slate-400 mt-2">
                  Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a> & <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white/50 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Invigor Digital. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
